// Platform helpers for Listings grid rows (release 2.1.2 listings model).
//
// A row is nested by table: { listings: { id, platform, ... },
// amazon_listings: {...}, walmart_listings: {...}, shopify_listings: {...} }.
// The extension tables are LEFT JOINed, so a Walmart row still carries an
// amazon_listings object, just with null values.

export type ListingPlatform = "amazon" | "walmart" | "shopify";

export const LISTING_PLATFORM_OPTIONS: {
  value: ListingPlatform;
  label: string;
}[] = [
  { value: "amazon", label: "Amazon" },
  { value: "walmart", label: "Walmart" },
  { value: "shopify", label: "Shopify" },
];

// Platform chip colors: each marketplace's brand color, with a text color that
// keeps WCAG AA contrast (white on Amazon orange does not, so it gets dark text)
export const LISTING_PLATFORM_COLORS: Record<
  ListingPlatform,
  { background: string; text: string }
> = {
  amazon: { background: "#FF9900", text: "#232F3E" },
  walmart: { background: "#0071DC", text: "#FFFFFF" },
  shopify: { background: "#008060", text: "#FFFFFF" },
};

/**
 * MUI Chip `sx` for a platform-coloured bubble, sized like the grid's
 * SingleSelectCell chips. Shared by the Platform column and the identifier
 * chips in Create Related Listing so they look the same.
 */
export function platformChipSx(platform: ListingPlatform) {
  const colors = LISTING_PLATFORM_COLORS[platform];
  return {
    height: 20,
    fontSize: "0.75rem",
    fontWeight: 600,
    bgcolor: colors.background,
    color: colors.text,
    // A clickable Chip's hover tint would grey the brand colour out
    "&:hover, &:focus-visible": {
      bgcolor: colors.background,
      filter: "brightness(0.92)",
    },
    "& .MuiChip-label": {
      px: 1,
    },
  } as const;
}

// listings.listing_origin values; NULL means created before 2.1.2
export const LISTING_ORIGIN_OPTIONS: { value: string; label: string }[] = [
  { value: "create_listing", label: "Create Listing" },
  { value: "create_related_listing", label: "Create Related Listing" },
  { value: "add_competitor_asin", label: "Competitor" },
  { value: "amazon_sync", label: "Amazon Sync" },
];

// Extension table → the only platform whose rows have that table's data
const PLATFORM_BY_TABLE: Record<string, ListingPlatform> = {
  amazon_listings: "amazon",
  walmart_listings: "walmart",
  shopify_listings: "shopify",
};

/**
 * The row's platform. A row without listings.platform (e.g. a nested panel
 * served without it) predates the platform split, so it counts as Amazon.
 */
export function getListingPlatform(row: any): ListingPlatform {
  const platform = row?.listings?.platform;
  return platform === "walmart" || platform === "shopify" ? platform : "amazon";
}

export function isAmazonListingRow(row: any): boolean {
  return getListingPlatform(row) === "amazon";
}

/** The platform a field's table belongs to, or undefined if it is shared. */
export function getFieldPlatform(field: string): ListingPlatform | undefined {
  const table = field.split(".")[0];
  return Object.prototype.hasOwnProperty.call(PLATFORM_BY_TABLE, table)
    ? PLATFORM_BY_TABLE[table]
    : undefined;
}

/**
 * Whether `field` may be edited on `row`: platform extension fields
 * (amazon_listings.*, walmart_listings.*, shopify_listings.*) only on rows of
 * that platform. The column's own `editable` flag still applies on top.
 */
export function isFieldEditableForRow(field: string, row: any): boolean {
  const fieldPlatform = getFieldPlatform(field);
  return !fieldPlatform || fieldPlatform === getListingPlatform(row);
}

/** For the grid's isCellEditable prop. */
export function isListingCellEditable(params: {
  field: string;
  row: any;
}): boolean {
  return isFieldEditableForRow(params.field, params.row);
}

export const AMAZON_ONLY_DISABLED_REASON =
  "Amazon only: selection has Walmart/Shopify rows";

export function hasNonAmazonRows(rows: any[]): boolean {
  return rows.some((row) => !isAmazonListingRow(row));
}
